import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Coffee, TrendingUp, Star, Users, Gift, Copy, Check,
  ArrowRight, ArrowLeft, Phone, LogOut, Download, Share2, Ticket, Award, Crown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import TouristVoucher from '@/components/TouristVoucher';
import { api } from '@/utils/apiClient';

export default function PartnerDashboardPage() {
  const [phone, setPhone] = useState('');
  const [loggedIn, setLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [copied, setCopied] = useState(false);

  // Check for saved session
  useEffect(() => {
    const savedPhone = localStorage.getItem('partner_phone');
    if (savedPhone) {
      setPhone(savedPhone);
      handleLogin(savedPhone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (phoneNumber = phone) => {
    if (!phoneNumber.trim()) {
      toast.error('Please enter your phone number');
      return;
    }

    setLoading(true);
    try {
      const res = await api.get(`/api/partners/dashboard/${encodeURIComponent(phoneNumber)}`);
      setDashboardData(res.data);
      setLoggedIn(true);
      localStorage.setItem('partner_phone', phoneNumber);
      
      // Generate QR Code
      if (res.data.partner?.code) {
        const qrUrl = await QRCode.toDataURL(
          `https://bulaccinocafe.com/order?partner=${res.data.partner.code}`,
          { width: 200, margin: 2 }
        );
        setQrCodeUrl(qrUrl);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Partner not found. Please sign up first.');
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('partner_phone');
    setLoggedIn(false);
    setDashboardData(null);
    setPhone('');
  };

  const copyCode = () => {
    if (dashboardData?.partner?.code) {
      navigator.clipboard.writeText(dashboardData.partner.code);
      setCopied(true);
      toast.success('Code copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const downloadQR = () => {
    if (qrCodeUrl) {
      const link = document.createElement('a');
      link.download = `bulaccino-partner-${dashboardData.partner.code}.png`;
      link.href = qrCodeUrl;
      link.click();
      toast.success('QR Code downloaded!');
    }
  };

  const shareCode = async () => {
    const code = dashboardData?.partner?.code;
    if (!code) return;
    
    const shareText = `Get 5% off at Bulaccino Cafe! Use my partner code: ${code} at checkout. Visit bulaccinocafe.com/order`;
    
    if (navigator.share) {
      try {
        await navigator.share({ text: shareText });
      } catch (err) {
        // User cancelled share - this is expected behavior, no action needed
        if (err.name !== 'AbortError') {
          console.warn('Share failed:', err.message);
        }
      }
    } else {
      navigator.clipboard.writeText(shareText);
      toast.success('Share text copied to clipboard!');
    }
  };

  // Login Screen
  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-background" data-testid="partner-login-page">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-md mx-auto">
            <Link to="/partner-program" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8">
              <ArrowLeft className="w-4 h-4" />
              Back to Partner Program
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card rounded-2xl p-8 border border-border shadow-sm"
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8 text-primary" />
                </div>
                <h1 className="text-2xl font-bold text-foreground mb-2">Partner Login</h1>
                <p className="text-muted-foreground">Enter your phone number to access your dashboard</p>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="e.g., 999 1234"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="mt-1"
                      autoFocus
                      data-testid="partner-login-phone"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    disabled={loading}
                    data-testid="partner-login-btn"
                  >
                    {loading ? 'Loading...' : 'View My Dashboard'}
                    {!loading && <ArrowRight className="ml-2 w-5 h-5" />}
                  </Button>
                </div>
              </form>

              <div className="mt-6 pt-6 border-t border-border text-center">
                <p className="text-sm text-muted-foreground">
                  Not a partner yet?{' '}
                  <Link to="/partner-signup" className="text-primary font-medium hover:underline">
                    Sign up free
                  </Link>
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  const { partner, stats, progress, recent_referrals } = dashboardData || {};

  return (
    <div className="min-h-screen bg-background" data-testid="partner-dashboard-page">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              Welcome, {partner?.name}!
            </h1>
            <p className="text-muted-foreground">Partner Dashboard</p>
          </div>
          <Button variant="outline" onClick={handleLogout} className="mt-4 md:mt-0">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Code & QR */}
          <div className="lg:col-span-1 space-y-6">
            {/* Partner Code Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-primary to-primary/80 text-white rounded-2xl p-6"
            >
              <p className="text-sm text-white/80 mb-2">Your Partner Code</p>
              <div className="flex items-center gap-3 mb-4">
                <p className="text-3xl font-mono font-bold tracking-wider" data-testid="dashboard-code">
                  {partner?.code}
                </p>
                <Button 
                  size="sm" 
                  variant="secondary"
                  onClick={copyCode}
                  className="bg-white/20 hover:bg-white/30 text-white"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-sm text-white/70">
                Tourists use this code to get 5% off
              </p>
            </motion.div>

            {/* QR Code Card */}
            {qrCodeUrl && (
              <div className="bg-card rounded-2xl p-6 border border-border text-center">
                <p className="text-sm font-medium text-foreground mb-4">Your QR Code</p>
                <img 
                  src={qrCodeUrl} 
                  alt="Partner QR Code" 
                  className="mx-auto rounded-xl bg-white p-2"
                  data-testid="partner-qr-code"
                />
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" size="sm" className="flex-1" onClick={downloadQR}>
                    <Download className="w-4 h-4 mr-1" />
                    Download
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1" onClick={shareCode}>
                    <Share2 className="w-4 h-4 mr-1" />
                    Share
                  </Button>
                </div>
              </div>
            )}

            {/* VIP Status */}
            {partner?.is_vip && (
              <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 rounded-2xl p-6 border border-amber-200 dark:border-amber-800">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-yellow-500 text-white flex items-center justify-center">
                    <Star className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-bold text-foreground">VIP Partner</p>
                    <p className="text-sm text-amber-600 dark:text-amber-400">10% off every purchase!</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Stats & Progress */}
          <div className="lg:col-span-2 space-y-6">
            {/* Stats Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-card rounded-xl p-4 border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats?.total_referrals || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Referrals</p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-xl p-4 border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats?.total_accumulated?.toFixed(0) || 0} FJD</p>
                    <p className="text-xs text-muted-foreground">Total Earned</p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-xl p-4 border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                    <Coffee className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats?.coffees_earned || 0}</p>
                    <p className="text-xs text-muted-foreground">Coffees Earned</p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-xl p-4 border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                    <Gift className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-primary">{stats?.coffees_available || 0}</p>
                    <p className="text-xs text-muted-foreground">Available to Redeem</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Cards */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Progress to Next Coffee */}
              <div className="bg-card rounded-xl p-6 border border-border">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-foreground">Next Free Coffee</h3>
                  <Coffee className="w-5 h-5 text-amber-500" />
                </div>
                <Progress value={progress?.to_next_coffee?.percentage || 0} className="h-3 mb-3" />
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {progress?.to_next_coffee?.current?.toFixed(0) || 0} / 100 FJD
                  </span>
                  <span className="text-primary font-medium">
                    {progress?.to_next_coffee?.remaining?.toFixed(0) || 100} FJD to go
                  </span>
                </div>
              </div>

              {/* Progress to VIP */}
              <div className={`rounded-xl p-6 border ${
                progress?.to_vip?.unlocked 
                  ? 'bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border-amber-200 dark:border-amber-800'
                  : 'bg-card border-border'
              }`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-foreground">VIP Status</h3>
                  <Star className={`w-5 h-5 ${progress?.to_vip?.unlocked ? 'text-amber-500' : 'text-muted-foreground'}`} />
                </div>
                {progress?.to_vip?.unlocked ? (
                  <div className="text-center py-2">
                    <p className="text-lg font-bold text-amber-600 dark:text-amber-400">VIP Unlocked!</p>
                    <p className="text-sm text-muted-foreground">Enjoy 10% off every purchase</p>
                  </div>
                ) : (
                  <>
                    <Progress value={progress?.to_vip?.percentage || 0} className="h-3 mb-3" />
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        {progress?.to_vip?.current?.toFixed(0) || 0} / 1,000 FJD
                      </span>
                      <span className="text-primary font-medium">
                        {progress?.to_vip?.remaining?.toFixed(0) || 1000} FJD to VIP
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Recent Referrals */}
            <div className="bg-card rounded-xl border border-border">
              <div className="p-4 border-b border-border">
                <h3 className="font-semibold text-foreground">Recent Referrals</h3>
              </div>
              <div className="p-4">
                {recent_referrals && recent_referrals.length > 0 ? (
                  <div className="space-y-3">
                    {recent_referrals.map((referral, index) => (
                      <div key={referral.id || index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {referral.purchase_amount?.toFixed(2)} FJD purchase
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(referral.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-green-600">
                            +{referral.purchase_amount?.toFixed(2)} FJD
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-muted-foreground">No referrals yet</p>
                    <p className="text-sm text-muted-foreground mt-1">Share your code to start earning!</p>
                  </div>
                )}
              </div>
            </div>

            {/* Tips Card */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
              <h3 className="font-semibold text-foreground mb-3">Tips to Earn More</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Tell tourists about the 5% discount - it's a great incentive!</li>
                <li>• Download and save your QR code for easy sharing</li>
                <li>• Mention Bulaccino has the best coffee in Nadi</li>
                <li>• Every 100 FJD in referrals = 1 free coffee for you</li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Tourist Voucher Section */}
        <div className="mt-12">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-2">
              <Ticket className="w-6 h-6 inline mr-2" />
              Tourist Voucher
            </h2>
            <p className="text-muted-foreground">
              Download or share this voucher with tourists. It explains the 5% discount and how to use your code.
            </p>
          </div>
          
          <TouristVoucher 
            partnerCode={dashboardData?.partner?.code || ''} 
            partnerName={dashboardData?.partner?.name || ''}
          />
        </div>
      </div>
    </div>
  );
}
